import * as React from 'react';
import { ArrowRight } from 'lucide-react';
import { t } from '~i18n';
import { StagedChange, StagedChanges } from './utils/stagedChanges';
import styles from './styles.module.css';
import { Spacer } from '~components/Utils/Spacer';

interface Props {
  changes: StagedChanges;
  /** What the save reaches: one device, a selection of them, or a site's device types. */
  appliesTo: string[];
  /**
   * How much unsaved work the action would throw away, where it is not the action saving it. A
   * dialog that claims to list everything the action does cannot leave this out.
   */
  discarding?: number;
}

const group = (titleKey: string, rows: StagedChange[]): React.ReactNode =>
  rows.length > 0 && (
    <div className={styles.summaryGroup}>
      <h4>{t(titleKey)}</h4>
      <ul>
        {rows.map((row) => (
          <li key={row.id}>
            <span className={styles.summaryLabel}>{row.label}</span>
            {row.to !== undefined && (
              <span className={styles.summaryValues}>
                {row.from !== undefined && (
                  <>
                    <s>{row.from}</s>
                    <ArrowRight size="0.9em" aria-hidden />
                  </>
                )}
                <b>{row.to}</b>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );

/**
 * What an action is about to write, for the dialog that asks first -- a save, or clearing the
 * device's own level.
 *
 * Grouped by what happens to the field rather than listed flat: an action can move a value, drop a
 * field out of the layer and pin another into it all at once, and the field names alone say none of
 * that. A field with no printable value is named without one, the same as its flag.
 */
const ChangeSummary: React.FC<Props> = ({ changes, appliesTo, discarding = 0 }) => (
  <div className={styles.summary}>
    <Spacer size={32} />
    {/* Only the list scrolls. The line naming what the save reaches has to stay in view, which is
        the whole reason it is in the dialog. */}
    <div className={styles.summaryList}>
      {group('device-config.change-summary.edited', changes.edited)}
      {group('device-config.change-summary.pinned', changes.pinned)}
      {group('device-config.change-summary.cleared', changes.cleared)}
    </div>
    {discarding > 0 && (
      <div className={styles.summaryWarning}>{t('device-config.change-summary.discards', { count: discarding })}</div>
    )}
    {/* A div rather than a paragraph: the action bar's modal styles its own `p`, and two
        single-class rules from different stylesheets would be settled by load order. */}
    {appliesTo.length > 0 && (
      <div className={styles.summaryScope}>
        {t('device-config.change-summary.applies-to')} {appliesTo.join(', ')}
      </div>
    )}
  </div>
);

export default ChangeSummary;
